// אופציית ECharts לבלוק chart — טהור (tokens כפרמטר, בלי DOM), נבדק ב-vitest. הבנייה עצמה ב-kit/series.js
// (cartesianOption), משותפת לחוקר הסדרות ול-iframe של בלוק app.
import type { EChartsCoreOption } from 'echarts/core';
import type { ChartBlock as ChartBlockT, ColType } from '@aibify/core';
import { fmtAxis, fmtCell } from './format.ts';
import { seriesStyles } from './series-style.ts';
import { MUTED_DARK, MUTED_LIGHT, RLM, cartesianOption, pieSlices, rankSeries, seriesModel, type KitTokens } from './kit/series.js';

export type ChartTokens = KitTokens & { pointer: string; dark: boolean };

/** RLM בתחילת תווית: "2005 · קירל" לא מתהפך בתוך SVG (v1, נמדד). */
const rtl = (s: unknown) => RLM + String(s ?? '');

export const FONT = 'Figtree, "Noto Sans Hebrew", system-ui, sans-serif';

/** רוחב (px) שמתחתיו עוגה עוברת לפריסה צרה: מקרא אנכי מתחת לעוגה, בלי תוויות חיצוניות (באג f5916b1f). */
export const PIE_NARROW_PX = 520;
const PIE_LEGEND_ROW = 22;

/**
 * פריסת עוגה צרה (טלפון): עוגה בראש, מקרא אנכי מתחתיה — שורה לכל פרוסה, בלי חפיפה. null = רחב (הפריסה הרגילה).
 * height = גובה המיכל הדרוש (העוגה + המקרא); ChartBlock מציב אותו על ה-div.
 */
export function pieLayout(width: number | undefined, slices: number): { d: number; legendTop: number; height: number } | null {
  if (!width || width >= PIE_NARROW_PX) return null;
  const d = Math.max(140, Math.min(width - 48, 240));
  const legendTop = d + 24;
  return { d, legendTop, height: legendTop + slices * PIE_LEGEND_ROW + 8 };
}

/** אופציית ECharts לבלוק chart — טהור (tokens כפרמטר), נבדק ב-vitest. */
/** focus = שם הסדרה שהעכבר עליה (ההדגשה נשלטת מכאן, לא ע"י emphasis של ECharts). width = רוחב המיכל (עוגה בטלפון). */
export function buildOption(block: ChartBlockT, t: ChartTokens, focus: string | null = null, width?: number): EChartsCoreOption {
  const unit: ColType = block.unit ?? 'number';
  const fmt = (n: unknown) => fmtCell(n, unit);
  const textStyle = { fontFamily: FONT, color: t.fg };
  if (block.chartType === 'pie') {
    const y = block.y[0]!;
    // יותר מ-8 פרוסות → 7 הגדולות + "אחרים"
    const slices = pieSlices(block.rows, block.x, y, 8);
    const narrow = pieLayout(width, slices.length);
    return {
      color: t.palette,
      textStyle,
      aria: { enabled: true },
      tooltip: {
        trigger: 'item',
        valueFormatter: fmt,
        backgroundColor: t.card,
        borderColor: t.border,
        borderWidth: 1,
        padding: [8, 12],
        textStyle: { ...textStyle, fontSize: 13 },
        extraCssText: 'border-radius:10px;box-shadow:0 12px 32px -12px rgba(2,8,20,.45);direction:rtl;text-align:right;',
      },
      legend: narrow
        ? {
            // טלפון: רשימה אנכית מתחת לעוגה, צמודה לימין (RTL) — השם המלא במקום תווית חיצונית שנחתכת
            type: 'plain',
            orient: 'vertical',
            top: narrow.legendTop,
            right: 16,
            align: 'right',
            icon: 'roundRect',
            itemWidth: 12,
            itemHeight: 8,
            itemGap: PIE_LEGEND_ROW - 14,
            textStyle: { color: t.muted, fontFamily: FONT, fontSize: 13, width: Math.max(80, width! - 64), overflow: 'truncate' },
            tooltip: { show: true },
          }
        : {
            bottom: 0,
            type: 'plain',
            icon: 'roundRect',
            itemWidth: 12,
            itemHeight: 8,
            itemGap: 16,
            textStyle: { color: t.muted, fontFamily: FONT, fontSize: 13 },
          },
      series: [
        {
          type: 'pie',
          radius: narrow ? [Math.round(narrow.d * 0.32), Math.round(narrow.d / 2)] : ['46%', '72%'],
          center: narrow ? ['50%', Math.round(narrow.d / 2) + 8] : ['50%', '46%'],
          padAngle: 1.5,
          itemStyle: { borderColor: t.card, borderWidth: 2, borderRadius: 4 },
          // בטלפון אין מקום לתוויות חיצוניות (נחתכות ל-"…" ונופלות על המקרא) — המקרא והחלונית נושאים את השם
          label: narrow ? { show: false } : { color: t.muted, fontFamily: FONT },
          labelLine: narrow ? { show: false } : { lineStyle: { color: t.border } },
          data: slices.map((s) => ({ name: rtl(s.name), value: s.value, ...(s.name === 'אחרים' && slices.length === 8 ? { itemStyle: { color: t.dark ? MUTED_DARK : MUTED_LIGHT } } : {}) })),
        },
      ],
    };
  }
  const model = seriesModel(block.rows, { x: block.x, y: block.y, seriesBy: block.seriesBy });
  // seriesBy: הסדרות לפי סה"כ יורד — הצבע לפי הדירוג; בלי seriesBy — סדר y (כמו שאייבי כתב)
  const ordered = block.seriesBy ? rankSeries(model.series) : model.series;
  const styles = seriesStyles({ y: ordered.map((s) => s.name), series: block.series }, t.palette, t.dark ? MUTED_DARK : MUTED_LIGHT);
  const m = { xs: model.xs, time: model.time, series: ordered.map((s, i) => ({ name: styles[i]!.name, data: s.data, color: styles[i]!.color, dash: styles[i]!.dash })) };
  const nameOf = (k: string) => styles.find((s) => s.key === k)?.name ?? k;
  return cartesianOption(m, t, {
    kind: block.chartType,
    stacked: block.stacked,
    fmt,
    axisFmt: fmtAxis,
    legend: m.series.length > 1,
    highlight: (block.highlight ?? []).map(nameOf),
    marks: block.marks?.map((mk) => (mk.series ? { ...mk, series: nameOf(mk.series) } : mk)),
    manualFocus: true,
    focus,
  });
}

/** העמודות שהבלוק צריך: x, y (או y[0] עם seriesBy), seriesBy. */
export const neededColumns = (b: ChartBlockT) => (b.seriesBy ? [b.x, b.seriesBy, b.y[0]!] : b.chartType === 'pie' ? [b.x, b.y[0]!] : [b.x, ...b.y]);

